import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../../hooks/useAuth';

// GQL
import { client } from '../../../../../gql/client';
import {
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
} from '../../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../../gql/queries/orders/predefinedData';
import { GET_USER_BY_ID } from '../../../../../gql/queries/users';

// Sentry
import * as Sentry from '@sentry/react';

// Data static
import { groupLanes, groupTracks } from '../../../../../data/orders';

// naming
import { selectOrderOfAccidentById } from '../../../../../gql/selectors/accidents';
import { selectActiveOperatorsAndVehicles } from '../../../../../gql/selectors/actions';
import { buildRegisteredByOperators } from '../../../../../utils/registeredByOperators';
import { formAccidentNaming } from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';

// Borradores con dueño
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACCIDENT_EDIT_SCOPE,
  ACCIDENT_MEDIA_SCOPE,
} from '../../../../../utils/draftScopes';
import { readAccidentEditCopy } from '../../../../../utils/accidentEditDraft';

const loadAccidentSummary = async (idOrder: string) => {
  try {
    const workOrderData = await selectOrderOfAccidentById(idOrder);

    const operatorData = await client.query({
      query: GET_USER_BY_ID,
      variables: {
        input: {
          id: workOrderData?.userId,
        },
      },
    });

    return {
      ...workOrderData,
      operator: `${operatorData.data.user.name} ${
        operatorData.data.user.surName.split(' ')[0]
      }`,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { classifications },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { subCapitols },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
      ]);

      return {
        ...data,
        capitols,
        classifications,
        groupTracks,
        groupLanes,
        predefinedOrders,
        subCapitols,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

export const EditAccidentWorkOrderLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
  // 🔴 Antes de cualquier `await`: los borradores y el croquis en curso tienen
  // que ser del accidente de la URL (`:id` = su Order). R5 del spec
  // wizard-draft-ownership: con atrás desde el lápiz de B, esta pantalla
  // montaba `{...servidor de A, ...copia de B}` y enseñaba B. Los tres
  // loaders del asistente reclaman lo mismo: el primero que corra descarta lo
  // ajeno y los demás no hacen nada (idempotente). El contexto de navegación
  // no se toca aquí: quien lo usa lo comprueba con su `entityId`
  // (`readOwnAccidentNavigationContext`).
  claimDraftScope(ACCIDENT_EDIT_SCOPE, params.id);
  claimDraftScope(ACCIDENT_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    let dataFromAccidentWorkOrder = {};

    if (params.id) {
      dataFromAccidentWorkOrder = await loadAccidentSummary(params.id);
    }

    // Tras el reclamo, la copia es de esta Order o no hay. Se comprueba igual
    // su `orderId` (cinturón y tirantes): una copia de otra Order no se mezcla
    // con el servidor de esta.
    const ownCopy = readAccidentEditCopy(
      formAccidentNaming.EDIT_ACCIDENT_FORM,
      params.id
    );

    const loaderToResolve = await loadFormOptions(userData);

    // Opciones del dropdown "Operario que registra" (mismo patrón que
    // NewAccidentLoader). Se añade SIEMPRE al final del combinedData para que
    // los operarios frescos ganen sobre cualquier copia persistida en sesión.
    let registeredByOperators;
    try {
      const { operators } = await selectActiveOperatorsAndVehicles(true);
      registeredByOperators = buildRegisteredByOperators(operators, userData);
    } catch {
      registeredByOperators = buildRegisteredByOperators([], userData);
    }

    if (ownCopy) {
      const combinedData = {
        ...loaderToResolve,
        ...dataFromAccidentWorkOrder,
        ...ownCopy,
        operators: registeredByOperators,
      };

      return combinedData;
    }

    const combinedData = {
      ...loaderToResolve,
      ...dataFromAccidentWorkOrder,
      operators: registeredByOperators,
      dataToCompare: dataFromAccidentWorkOrder,
    };

    return combinedData;
  };

  return { data: load() };
};
